import { UIEvent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import './attendance.scss';

import { useJournalList } from '~modules/journal/api/use-journal-list';
import { JournalDto, translateJournalType } from '~modules/journal/model';
import { loadUserById, searchUserByAvailableSections } from '~modules/user/api';
import { UserDto } from '~modules/user/model';
import rightIcon from '~shared/assets/icons/right.svg';
import { Breadcrumbs } from '~shared/ui/breadcrumbs/breadcrumbs';
import NewTable, { INewTableColumn } from '~shared/ui/new-table/new-table';

import { AttendanceFilters } from './attendance-filters';

export function AttendancePage() {
	const JOURNAL_PAGE_LIMIT = 20;
	const [userFilterResetKey, setUserFilterResetKey] = useState(0);
	const [users, setUsers] = useState<UserDto[]>([]);
	const [usersLoading, setUsersLoading] = useState(false);
	const [userSearch, setUserSearch] = useState('');
	const [chunkPage, setChunkPage] = useState(1);
	const [rows, setRows] = useState<JournalDto[]>([]);
	const [loadingMore, setLoadingMore] = useState(false);
	const { userId } = useParams<{ userId?: string }>();
	const [selectedUser, setSelectedUser] = useState<UserDto | undefined>();

	useEffect(() => {
		if (!userId) return;
		const id = Number(userId);
		if (Number.isNaN(id)) return;
		loadUserById(id)
			.then(user => {
				if (user) setSelectedUser(user);
			})
			.catch(console.error);
	}, [userId]);

	const [datePeriod, setDatePeriod] = useState<{
		startDate: Date | null;
		endDate: Date | null;
	}>({
		startDate: null,
		endDate: null,
	});

	useEffect(() => {
		const timeout = setTimeout(() => {
			setUsersLoading(true);
			searchUserByAvailableSections(userSearch)
				.then(data => setUsers(data))
				.catch(console.error)
				.finally(() => setUsersLoading(false));
		}, 300);

		return () => clearTimeout(timeout);
	}, [userSearch]);

	const offset = (chunkPage - 1) * JOURNAL_PAGE_LIMIT;
	const {
		list: journalChunk,
		total,
		pending,
	} = useJournalList(datePeriod.startDate, datePeriod.endDate, selectedUser?.id, JOURNAL_PAGE_LIMIT, offset);

	const userOptions = useMemo(() => {
		if (!selectedUser) return users;
		return users.some(user => user.id === selectedUser.id) ? users : [selectedUser, ...users];
	}, [selectedUser, users]);

	useEffect(() => {
		setChunkPage(1);
		setRows([]);
		setLoadingMore(false);
	}, [selectedUser?.id, datePeriod.startDate, datePeriod.endDate]);

	useEffect(() => {
		if (chunkPage === 1) {
			setRows(journalChunk);
			return;
		}
		setRows(prevRows => {
			const existingIds = new Set(prevRows.map(row => row.id));
			const nextRows = journalChunk.filter(row => !existingIds.has(row.id));
			return [...prevRows, ...nextRows];
		});
	}, [journalChunk, chunkPage]);

	useEffect(() => {
		if (!pending) {
			setLoadingMore(false);
		}
	}, [pending]);

	const handleResetFilters = () => {
		setUserFilterResetKey(prev => prev + 1);
		setSelectedUser(undefined);
		setUserSearch('');
		setDatePeriod({
			startDate: null,
			endDate: null,
		});
		setChunkPage(1);
		setRows([]);
		setLoadingMore(false);
	};

	const handleTableScroll = (event: UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const nearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 120;
		const canLoadMore = rows.length < total && !pending && !loadingMore;
		if (nearBottom && canLoadMore) {
			setLoadingMore(true);
			setChunkPage(prevPage => prevPage + 1);
		}
	};

	const columns: INewTableColumn<JournalDto>[] = [
		{
			key: 'user',
			title: 'Сотрудник',
			render: (row: JournalDto) => row.user?.name ?? '-',
		},
		{
			key: 'date',
			title: 'Дата',
			render: (row: JournalDto) => row.date.toLocaleString('ru-RU'),
		},
		{
			key: 'type',
			title: 'Тип',
			render: (row: JournalDto) => translateJournalType(row.type),
		},
		{
			key: 'section',
			title: 'Участок',
			render: (row: JournalDto) => row.user?.section?.title ?? '-',
		},
		{
			key: 'manager',
			title: 'Менеджер',
			render: (row: JournalDto) => row.manager?.name ?? '-',
		},
	];

	return (
		<div className="page page-background">
			<div className="page-container attendance-page-container">
				<Breadcrumbs
					className="attendance-breadcrumbs"
					separatorIconSrc={rightIcon}
					items={[{ label: 'Табелирование' }, { label: 'Отметки сотрудников' }]}
				/>
				<h2 className="page-title" style={{ marginBottom: '40px' }}>
					Просмотр отметок сотрудников
				</h2>
				<AttendanceFilters
					userFilterResetKey={userFilterResetKey}
					userOptions={userOptions}
					selectedUser={selectedUser}
					userSearch={userSearch}
					usersLoading={usersLoading}
					datePeriod={datePeriod}
					onUserChange={setSelectedUser}
					onUserSearchChange={setUserSearch}
					onDatePeriodChange={(startDate, endDate) => setDatePeriod({ startDate, endDate })}
					onReset={handleResetFilters}
				/>
				<div className="attendance-table-scroll">
					<NewTable<JournalDto>
						columns={columns}
						rows={rows}
						pending={pending}
						emptyText="Нет данных"
						onScroll={handleTableScroll}
					/>
				</div>
			</div>
		</div>
	);
}
